import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { spinnerService, SpinnerData } from '../../services/spinnerService';
import { format } from 'date-fns';
import { Share2Icon, Trash2Icon, FolderOpenIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'react-hot-toast';
import { MAX_SAVED_SPINNERS } from '../../config/appProperties';
import { auth } from '../../firebase/config';

interface SavedSpinnersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShare: (spinnerId: string) => void;
  onOpen?: (spinnerId: string) => void;
  onLoginRequest?: () => void;
}

export const SavedSpinnersModal: React.FC<SavedSpinnersModalProps> = (props) => {
  const { isOpen, onClose, onShare, onOpen, onLoginRequest } = props;
  const { t } = useTranslation();
  const [spinners, setSpinners] = useState<SpinnerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(auth.currentUser);

  useEffect(() => {
    if (isOpen) {
      setUser(auth.currentUser);
      if (auth.currentUser) {
        loadSpinners();
      } else {
        setLoading(false);
      }
    }
  }, [isOpen]);

  const loadSpinners = async () => {
    try {
      setLoading(true);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setLoading(false);
        return;
      }
      const userSpinners = await spinnerService.getUserSpinners(currentUser.uid);
      setSpinners(userSpinners);
    } catch (error) {
      console.error('Error loading spinners:', error);
      toast.error(t('savedSpinners.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await spinnerService.deleteSpinner(id);
      setSpinners(prev => prev.filter(s => s.id !== id));
      toast.success(t('savedSpinners.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting spinner:', error);
      toast.error(t('savedSpinners.deleteError'));
    }
  };

  const handleLoginClick = () => {
    onClose();
    if (onLoginRequest) {
      onLoginRequest();
    }
  };

  if (!isOpen) return null;

  // Prevent adding more than MAX_SAVED_SPINNERS
  const isLimitReached = spinners.length >= MAX_SAVED_SPINNERS;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-4 w-full max-w-[440px] mx-4 sm:mx-2 overflow-x-auto shadow-lg"
        style={{ minWidth: 0 }}
      >
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            {t('savedSpinners.title', 'Saved Spinners')}
          </h2>
          <button
            onClick={onClose}
            className="text-2xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 px-2"
            style={{ fontSize: '1.5rem', lineHeight: '1.5rem' }}
          >
            ×
          </button>
        </div>
        
        {!user ? (
          // Not logged in state
          <div className="text-center py-2">
            <div className="text-gray-500 dark:text-gray-400 mb-4">
              <p className="text-lg font-medium mb-2">{t('savedSpinners.loginToView', 'Login to view your saved spinners')}</p>
              <p className="text-sm">{t('savedSpinners.signInToAccess', 'Sign in to access your saved spinners or create new ones')}</p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={handleLoginClick}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {t('auth.login', 'Login')}
              </Button>
              <Button 
                onClick={onClose}
                variant="outline"
              >
                {t('common.close', 'Close')}
              </Button>
            </div>
          </div>
        ) : loading ? (
          // Loading state
          <div className="text-center py-2">{t('common.loading', 'Loading...')}</div>
        ) : spinners.length === 0 ? (
          // No spinners state
          <div className="text-center py-8">
            <div className="text-gray-500 dark:text-gray-400 mb-4">
              <p className="text-lg font-medium mb-2">{t('savedSpinners.noSpinners', 'No saved spinners')}</p>
              <p className="text-sm">{t('savedSpinners.createAndShare', 'Create and share spinners to see them here')}</p>
            </div>
            <Button onClick={onClose}>
              {t('common.close', 'Close')}
            </Button>
          </div>
        ) : (
          // Spinners list state
          <>
            <div className="max-h-96 overflow-x-auto">
              <table className="text-xs table-auto" style={{maxWidth: '100%', width: 'auto'}}>
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 text-left font-bold text-gray-600 dark:text-gray-300" style={{minWidth: '80px', maxWidth: '36ch', width: '36ch'}}>{t('savedSpinners.name', 'Name')}</th>
                    <th className="px-1 py-2 text-center font-bold text-gray-600 dark:text-gray-300 whitespace-nowrap" style={{width: '110px'}}>{t('savedSpinners.actions', 'Actions')}</th>
                    <th className="px-1 py-2 text-left font-bold text-gray-600 dark:text-gray-300 whitespace-nowrap" style={{minWidth: '110px', width: '110px'}}>{t('savedSpinners.expires', 'Expires On')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {spinners.map((spinner) => (
                    <tr key={spinner.id}>
                      <td className="px-2 py-1 whitespace-nowrap text-xs text-gray-900 dark:text-white truncate" style={{maxWidth: '36ch', width: '36ch'}}>
                        {spinner.name}
                      </td>
                      <td className="px-1 py-1 whitespace-nowrap text-center text-xs font-medium flex items-center justify-center gap-0.5 pr-3" style={{width: '110px'}}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (onOpen) onOpen(spinner.id);
                            onClose();
                          }}
                          title={t('savedSpinners.open', 'Open')}
                        >
                          <FolderOpenIcon size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (onOpen) onOpen(spinner.id);
                            onShare(spinner.id);
                            onClose();
                          }}
                          title={t('savedSpinners.share', 'Share')}
                        >
                          <Share2Icon size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(spinner.id)}
                          title={t('savedSpinners.delete', 'Delete')}
                        >
                          <Trash2Icon size={14} />
                        </Button>
                      </td>
                      <td className="px-1 py-1 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400" style={{width: '110px'}}>
                        {format(spinner.expiresAt, 'P p')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 border-t pt-3">
              <p className="font-medium mb-1">{t('savedSpinners.note', 'Note:')}</p>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('savedSpinners.unusedExpire', 'Unused spinner links expire after 7 days')}</li>
                <li>{t('savedSpinners.activeExpire', 'Active spinner links remain valid for 365 days')}</li>
              </ul>
            </div>
            <div className="mt-3 flex justify-end">
              <Button onClick={onClose}>
                {t('common.close', 'Close')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}; 